import React from 'react'
// libs
import { saveAs } from 'file-saver'
import { useSelector } from 'react-redux'
import {
  Packer,
  Header,
  Footer,
  TextRun,
  ImageRun,
  Document,
  Paragraph,
  PageOrientation,
  ExternalHyperlink
} from 'docx'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useApolloClient } from 'hooks/useApolloClient'
// store
import { responseTypesSelector } from 'store/reducers/riskTemplates'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { FieldType, IFieldLink, IFieldValueMapItem, IRisk, IRiskTemplate } from 'types/graphql'
// graphql
import { RISKS_EXPORT_QUERY } from 'graphql/risk/queries'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { getFieldsOptions } from 'utils/form'
import { applyLogicalConditions, generateFields } from './utils'

type RiskToExport = { riskId: string; hasParent: boolean; riskTemplateId: string }

export const useRiskExport = () => {
  const user = useCurrentUser()

  const { client } = useApolloClient()

  const { risk } = React.useContext(RiskAPIContext)
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const responseTypeSets = useSelector(responseTypesSelector)

  const [loading, setLoading] = React.useState(false)

  const fetchImage = async (url: string) => {
    try {
      const response = await fetch(url).catch((err) => handleError(err, 'useRiskExport.fetchImage'))

      if (!response) {
        return
      }

      const blob = await response.blob()

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(blob)

        reader.onloadend = () => {
          resolve(reader.result)
        }

        reader.onerror = () => {
          reject('[useRiskExport.fetchImage] Error reading blob')
        }
      })
    } catch (err) {
      console.error('[useRiskExport.fetchImage]', err)
    }
  }

  const getRisksData = async (risks: RiskToExport[], excludeLabels: boolean) => {
    try {
      // the callback below shadows `risk` with the fetched record, so read the page's own parent here
      const parentRiskId = risk?.parentRiskId

      const data = await Promise.all(
        risks.map(async ({ riskId, hasParent, riskTemplateId }) => {
          const response = await client.query({
            fetchPolicy: 'no-cache',
            query: RISKS_EXPORT_QUERY,
            variables: {
              filter: {
                ids: [riskId],
                riskTemplateId,
                ...(hasParent && { parentRiskId })
              }
            }
          })

          const risk: IRisk = get(response, 'data.risks.data[0]', null)

          if (!risk) return null

          const fieldLinksFields = get(risk, 'riskTemplate.fieldLinks', []).map((item: IFieldLink) => ({
            ...item.field,
            parentFieldId: item.parentFieldId,
            position: item.position,
            label: item.fieldLabel,
            fieldLinkId: item.id,
            primary: item.primary
          }))

          const fields = [
            ...applyLogicalConditions(
              risk.riskTemplate.fields.filter((item) => !!item.active),
              risk.fieldValueMap,
              risk.riskTemplate.formConditions
            ),
            ...fieldLinksFields
          ]

          const values = get(risk, 'fieldValueMap', [])
            .filter(({ fieldId }) => fields.some(({ id }) => id === fieldId))
            .map((item) => {
              if (item.linkedFieldValue) {
                return {
                  ...item,
                  fieldValue: item.linkedFieldValue?.fieldValue,
                  linkedRiskId: item.fieldValue.value,
                  linkedFieldValueId: item.linkedFieldValue?.fieldValueId
                }
              }
              return item
            }) as IFieldValueMapItem[]

          const lookUpOptions = await getFieldsOptions({ client, fields, fieldsValues: values, isRisk: true })

          const preparedFields = fields
            .map((field) => {
              if (field.fieldType === FieldType.Lookup) {
                return { ...field, options: lookUpOptions }
              }

              if (field.fieldType === FieldType.PickList && field.responseTypeId) {
                return {
                  ...field,
                  options: responseTypeSets.find((item) => item.id == field.responseTypeId)?.options || []
                }
              }

              return field
            })
            .filter((field) => !(excludeLabels && field.fieldType === FieldType.Label))

          return {
            risk,
            values,
            riskTemplate: {
              ...risk.riskTemplate,
              fields: preparedFields
            }
          }
        })
      )

      return data.filter(Boolean)
    } catch (e) {
      handleError(e, 'Error fetching risks')
    }
  }

  const generateDocument = async (risks: RiskToExport[], excludeLabels: boolean) => {
    try {
      setLoading(true)

      const data = await getRisksData(risks, excludeLabels)
      const skeftoLogo = (await fetchImage('/images/logo.png')) as string
      const companyLogo = user.company.logoUrl ? ((await fetchImage(user.company.logoUrl)) as string) : null

      const sections = data.map(({ risk, riskTemplate, values }) => {
        const hasParent = !!risk.parentRisk

        let headerText: string

        if (riskTemplate.customerUniqueIdPrefix && !hasParent) {
          headerText = `${riskTemplate.name} - ${riskTemplate.customerUniqueIdPrefix}${risk.customerUniqueIdPostfix}`
        } else if (hasParent) {
          const parentTemplate = risk.parentRisk.riskTemplate

          headerText = parentTemplate.customerUniqueIdPrefix
            ? `${riskTemplate.name} - ${parentTemplate.customerUniqueIdPrefix}${risk.parentRisk.customerUniqueIdPostfix}`
            : riskTemplate.name
        } else {
          headerText = riskTemplate.name
        }

        return {
          properties: {
            page: {
              pageNumbers: { start: 1 },
              size: { orientation: PageOrientation.LANDSCAPE },
              margin: { top: 0, bottom: 0 }
            }
          },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: headerText,
                      size: 40,
                      bold: true,
                      font: 'Arial'
                    })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 200 },
                  children: [
                    new TextRun({ text: 'URL: ', bold: true, font: 'Arial' }),
                    new ExternalHyperlink({
                      children: [new TextRun({ text: window.location.href, style: 'Hyperlink', font: 'Arial' })],
                      link: window.location.href
                    })
                  ]
                })
              ]
            })
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  shading: { fill: '#FFFFFF' },
                  children: [
                    new ImageRun({
                      type: 'png',
                      data: companyLogo || skeftoLogo,
                      transformation: { width: companyLogo ? 80 : 110, height: companyLogo ? 80 : 25 }
                    })
                  ]
                })
              ]
            })
          },
          children: [
            generateFields({
              riskTemplate: riskTemplate as IRiskTemplate,
              fields: riskTemplate.fields as any,
              fieldValues: values
            })
          ]
        }
      })

      const doc = new Document({ sections })
      const blob = await Packer.toBlob(doc)

      const parentData = data.find(
        (item) => item.riskTemplate.id === (risk?.parentRisk?.riskTemplate?.id || riskTemplate.id)
      ) || {
        risk,
        riskTemplate
      }

      saveAs(
        blob,
        `${
          parentData.riskTemplate.customerUniqueIdPrefix
            ? `${parentData.riskTemplate.customerUniqueIdPrefix}${parentData.risk.customerUniqueIdPostfix} - ${parentData.riskTemplate.name}`
            : parentData.riskTemplate.name
        }.docx`
      )
    } catch (err) {
      handleError(err, 'useRiskExport.generateDocument')
    } finally {
      setLoading(false)
    }
  }

  return { loading, generateDocument }
}
